import React, { useState, useEffect } from 'react';
import { rolePermissionsAPI } from '../services/rolePermissionsAPI';
import ConfirmModal from './ConfirmModal.jsx';
import '../styles/RoleManagement.css';

// Mirrors the actual main navigation structure in frontend/src/components/Navbar.jsx.
// Each nav tab lists the permission-name resource prefixes that gate its visibility,
// so the master checkbox below can reliably show/hide that exact nav item.
const NAV_TABS = [
  { id: 'marketing', title: 'Marketing', resources: ['contacts', 'templates', 'template-images', 'campaigns', 'contact-lists', 'queue', 'analytics'] },
  { id: 'events', title: 'Events', resources: ['events'] },
  { id: 'website', title: 'Website', resources: ['news', 'careers', 'team-members'] },
  { id: 'rate-calculator', title: 'Rate Calculator', resources: ['rate-calculator', 'rate-clients'] },
  { id: 'ncf', title: 'NCFs', resources: ['ncf'] },
  { id: 'signature-builder', title: 'Signature Builder', resources: ['signature-builder'] },
  { id: 'video-backgrounds', title: 'Meeting Backgrounds', resources: ['video-backgrounds'] },
  { id: 'admin', title: 'Admin', resources: ['admin'] }
];

const RESOURCE_TO_NAV_TAB = NAV_TABS.reduce((acc, tab) => {
  tab.resources.forEach(resource => { acc[resource] = tab.id; });
  return acc;
}, {});

const NAV_TAB_ORDER = [...NAV_TABS.map(tab => tab.id), 'other'];
const NAV_TAB_TITLES = NAV_TABS.reduce((acc, tab) => ({ ...acc, [tab.id]: tab.title }), { other: 'Other' });

const PERMISSION_LABEL_OVERRIDES = {
  'ncf:admin:access': 'NCF Admin (Full Access)',
  'rate-calculator:use': 'Rate Calculator',
  'rate-clients:read': 'Manage Clients (View)',
  'rate-clients:write': 'Manage Clients (Edit)'
};

const getPermissionLabel = (permissionId = '', defaultLabel = '') => {
  const normalizedId = String(permissionId || '').trim().toLowerCase();
  const explicitLabel = PERMISSION_LABEL_OVERRIDES[normalizedId];
  if (explicitLabel) {
    return explicitLabel;
  }

  return defaultLabel || permissionId;
};

const getNavTabId = (permissionId = '') => {
  const normalizedId = String(permissionId || '').trim().toLowerCase();
  if (!normalizedId) return 'other';

  const [resource] = normalizedId.split(':');
  return RESOURCE_TO_NAV_TAB[resource] || 'other';
};

const groupPermissionsByNavTab = (permissions = []) => {
  const grouped = permissions.reduce((acc, permissionItem) => {
    const navTabId = getNavTabId(permissionItem.id);
    if (!acc[navTabId]) {
      acc[navTabId] = [];
    }
    acc[navTabId].push(permissionItem);
    return acc;
  }, {});

  const sortedTabIds = Object.keys(grouped).sort((a, b) => {
    const indexA = NAV_TAB_ORDER.indexOf(a);
    const indexB = NAV_TAB_ORDER.indexOf(b);

    const safeIndexA = indexA === -1 ? NAV_TAB_ORDER.length : indexA;
    const safeIndexB = indexB === -1 ? NAV_TAB_ORDER.length : indexB;

    if (safeIndexA !== safeIndexB) return safeIndexA - safeIndexB;
    return a.localeCompare(b);
  });

  return sortedTabIds.map(navTabId => ({
    area: navTabId,
    title: NAV_TAB_TITLES[navTabId] || navTabId,
    permissions: [...grouped[navTabId]].sort((a, b) => a.label.localeCompare(b.label))
  }));
};

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [showNewRoleForm, setShowNewRoleForm] = useState(false);
  const [rolePermissions, setRolePermissions] = useState({});
  const [roleToDelete, setRoleToDelete] = useState(null);
  const groupedPermissions = groupPermissionsByNavTab(availableTabs);

  useEffect(() => {
    loadRolesAndTabs();
  }, []);

  const loadRolesAndTabs = async () => {
    try {
      setLoading(true);
      const rolesRes = await rolePermissionsAPI.getRoles();
      const tabsRes = await rolePermissionsAPI.getAvailableTabs();
      
      setRoles(rolesRes.data);
      const normalizedPermissions = (tabsRes.data || []).map((permissionItem) => ({
        id: permissionItem.name,
        label: getPermissionLabel(permissionItem.name, permissionItem.description || permissionItem.name)
      }));
      setAvailableTabs(normalizedPermissions);
      
      // Initialize rolePermissions state
      const permsMap = {};
      rolesRes.data.forEach(r => {
        permsMap[r.role] = r.permissions || [];
      });
      setRolePermissions(permsMap);
    } catch (err) {
      setError('Failed to load roles and tabs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabToggle = (role, tabId) => {
    setRolePermissions(prev => {
      const current = prev[role] || [];
      const updated = current.includes(tabId)
        ? current.filter(t => t !== tabId)
        : [...current, tabId];
      return { ...prev, [role]: updated };
    });
  };

  // Master checkbox per nav tab: unchecking hides the tab entirely (clears every
  // permission in the group); checking grants the full group so the tab appears.
  const handleNavTabMasterToggle = (role, group) => {
    setRolePermissions(prev => {
      const current = prev[role] || [];
      const groupIds = group.permissions.map(item => item.id);
      const anyChecked = groupIds.some(id => current.includes(id));
      const updated = anyChecked
        ? current.filter(id => !groupIds.includes(id))
        : Array.from(new Set([...current, ...groupIds]));
      return { ...prev, [role]: updated };
    });
  };

  const handleSavePermissions = async (role) => {
    try {
      setLoading(true);
      await rolePermissionsAPI.updateRolePermissions(role, rolePermissions[role] || []);
      setSuccessMessage(`Permissions saved for role: ${role}`);
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to save permissions: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      setError('Role name is required');
      return;
    }

    try {
      setLoading(true);
      await rolePermissionsAPI.createRole(newRoleName, []);
      setSuccessMessage(`Role "${newRoleName}" created successfully`);
      setNewRoleName('');
      setShowNewRoleForm(false);
      await loadRolesAndTabs();
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to create role: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (['admin', 'superuser', 'user'].includes(role)) {
      setError('Cannot delete built-in roles');
      return;
    }

    setRoleToDelete(role);
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      setLoading(true);
      await rolePermissionsAPI.deleteRole(roleToDelete);
      setSuccessMessage(`Role "${roleToDelete}" deleted successfully`);
      setSelectedRole(null);
      await loadRolesAndTabs();
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to delete role: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
      setRoleToDelete(null);
    }
  };

  if (loading) return <div className="role-management"><p>Loading...</p></div>;

  return (
    <div className="role-management">
      <ConfirmModal
        show={Boolean(roleToDelete)}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${roleToDelete || ''}"?`}
        onConfirm={confirmDeleteRole}
        onCancel={() => setRoleToDelete(null)}
        confirmText="Delete"
        confirmStyle="danger"
      />

      <h2>Role & Permission Management</h2>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      <div className="role-management-container">
        <div className="roles-list">
          <h3>Roles</h3>
          <div className="role-buttons">
            {roles.map(r => (
              <button
                key={r.role}
                className={`role-btn ${selectedRole === r.role ? 'active' : ''}`}
                onClick={() => setSelectedRole(r.role)}
              >
                {r.role}
              </button>
            ))}
          </div>
          
          {!showNewRoleForm ? (
            <button 
              className="btn btn-primary btn-sm"
              onClick={() => setShowNewRoleForm(true)}
              style={{ marginTop: '10px' }}
            >
              + Create New Role
            </button>
          ) : (
            <div className="new-role-form">
              <input
                type="text"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="Enter role name"
                className="form-input"
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button className="btn btn-success btn-sm" onClick={handleCreateRole}>
                  Create
                </button>
                <button 
                  className="btn btn-cancel btn-sm" 
                  onClick={() => { setShowNewRoleForm(false); setNewRoleName(''); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="permissions-editor">
          {selectedRole ? (
            <>
              <h3>Permissions for: <strong>{selectedRole}</strong></h3>

              {groupedPermissions.map(group => {
                const rolePermissionIds = rolePermissions[selectedRole] || [];
                const groupIds = group.permissions.map(item => item.id);
                const isTabVisible = groupIds.some(id => rolePermissionIds.includes(id));
                return (
                  <section key={group.area} className="permission-group">
                    <h4 className="permission-group-title">
                      <label className="permission-group-master" title="Show/hide this tab in the navigation">
                        <input
                          type="checkbox"
                          checked={isTabVisible}
                          onChange={() => handleNavTabMasterToggle(selectedRole, group)}
                          disabled={loading}
                        />
                        <span>{group.title}</span>
                      </label>
                    </h4>
                    <div className="permissions-grid">
                      {group.permissions.map(tab => (
                        <label key={tab.id} className="permission-checkbox">
                          <input
                            type="checkbox"
                            checked={rolePermissionIds.includes(tab.id)}
                            onChange={() => handleTabToggle(selectedRole, tab.id)}
                            disabled={loading}
                          />
                          <span>{tab.label}</span>
                        </label>
                      ))}
                    </div>
                  </section>
                );
              })}

              <div className="permission-actions" style={{ marginTop: '20px' }}>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleSavePermissions(selectedRole)}
                  disabled={loading}
                >
                  Save Permissions
                </button>
                
                {!['admin', 'superuser', 'user'].includes(selectedRole) && (
                  <button 
                    className="btn btn-danger"
                    onClick={() => handleDeleteRole(selectedRole)}
                    disabled={loading}
                  >
                    Delete Role
                  </button>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
              <p>Select a role to manage permissions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
